"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useCatalogViewport } from "./catalogViewport";

export type BeforeAfterPair = { before: string; after: string };

interface BeforeAfterCyclePreviewProps {
  pairs: BeforeAfterPair[];
  className?: string;
  alt?: string;
  /** Hold original (slider at 0%) before the sweep starts. */
  holdMs?: number;
  /** Time for the divider to travel one way. */
  sweepMs?: number;
}

function pairKey(pair: BeforeAfterPair): string {
  return `${pair.before}|${pair.after}`;
}

/**
 * Poster is the first before (normal <img>). On screen we sweep each pair,
 * then advance through every remaining pair. Off-screen cards stay on the poster.
 */
export default function BeforeAfterCyclePreview({
  pairs,
  className = "",
  alt = "",
  holdMs = 800,
  sweepMs = 1400,
}: BeforeAfterCyclePreviewProps) {
  const valid = useMemo(() => pairs.filter((p) => p.before && p.after), [pairs]);
  const { ref, inView } = useCatalogViewport();
  const [pairIndex, setPairIndex] = useState(0);
  const [position, setPosition] = useState(0);
  const [transitionMs, setTransitionMs] = useState(0);
  const readyRef = useRef<Record<string, boolean>>({});
  const [readyTick, setReadyTick] = useState(0);

  const safeIndex = valid.length === 0 ? 0 : pairIndex % valid.length;
  const pair = valid[safeIndex];
  const afterReady = Boolean(pair && readyRef.current[pair.after]);

  const markReady = (src: string) => {
    if (!src || readyRef.current[src]) return;
    readyRef.current[src] = true;
    setReadyTick((n) => n + 1);
  };

  useEffect(() => {
    if (inView) return;
    setPairIndex(0);
    setPosition(0);
    setTransitionMs(0);
  }, [inView]);

  useEffect(() => {
    if (!inView || !pair || !afterReady) return;
    let cancelled = false;
    const timers: number[] = [];
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timers.push(window.setTimeout(resolve, ms));
      });

    const run = async () => {
      while (!cancelled) {
        setPosition(0);
        setTransitionMs(0);
        await wait(holdMs);
        if (cancelled) return;

        setTransitionMs(sweepMs);
        setPosition(100);
        await wait(sweepMs + 350);
        if (cancelled) return;

        setTransitionMs(sweepMs);
        setPosition(0);
        await wait(sweepMs + 200);
        if (cancelled) return;

        if (valid.length <= 1) continue;

        let nextIndex = -1;
        for (let step = 1; step <= valid.length; step += 1) {
          const next = (safeIndex + step) % valid.length;
          const candidate = valid[next];
          if (readyRef.current[candidate.before] && readyRef.current[candidate.after]) {
            nextIndex = next;
            break;
          }
        }
        if (nextIndex >= 0 && nextIndex !== safeIndex) {
          setPairIndex(nextIndex);
          return;
        }
      }
    };

    run();
    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [inView, afterReady, safeIndex, pair?.before, pair?.after, valid, holdMs, sweepMs]);

  if (!pair) {
    return <div className={`h-full w-full bg-gray-100 ${className}`} />;
  }

  const rightClip = Math.max(0, Math.min(100, 100 - position));
  const showAfter = inView && afterReady;
  void readyTick;

  return (
    <div
      ref={ref}
      className={`relative h-full w-full overflow-hidden bg-gray-100 select-none ${className}`}
    >
      <img
        src={pair.before}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover object-top"
        loading={safeIndex === 0 ? "lazy" : "eager"}
        decoding="async"
        draggable={false}
        onLoad={() => markReady(pair.before)}
        ref={(el) => {
          if (el?.complete) markReady(pair.before);
        }}
      />
      {inView ? (
        <img
          src={pair.after}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-top"
          style={{
            clipPath: `inset(0 ${rightClip}% 0 0)`,
            transition: transitionMs ? `clip-path ${transitionMs}ms ease-in-out` : "none",
            opacity: showAfter ? 1 : 0,
          }}
          decoding="async"
          draggable={false}
          aria-hidden
          onLoad={() => markReady(pair.after)}
          ref={(el) => {
            if (el?.complete) markReady(pair.after);
          }}
        />
      ) : null}
      {showAfter ? (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_8px_rgba(0,0,0,0.35)] pointer-events-none"
          style={{
            left: `${position}%`,
            transform: "translateX(-50%)",
            transition: transitionMs ? `left ${transitionMs}ms ease-in-out` : "none",
          }}
        />
      ) : null}
      {inView
        ? valid.map((p, i) =>
            i === safeIndex ? null : (
              <div key={pairKey(p)} className="hidden" aria-hidden>
                <img
                  src={p.before}
                  alt=""
                  decoding="async"
                  onLoad={() => markReady(p.before)}
                  ref={(el) => {
                    if (el?.complete) markReady(p.before);
                  }}
                />
                <img
                  src={p.after}
                  alt=""
                  decoding="async"
                  onLoad={() => markReady(p.after)}
                  ref={(el) => {
                    if (el?.complete) markReady(p.after);
                  }}
                />
              </div>
            )
          )
        : null}
    </div>
  );
}
